import React, { Component } from "react";
import "./Grading.css";
import { AnimatePresence } from "framer-motion";
import NewPdfViewer from "../Simulation/Popups/NewPdfViewer/NewPdfViewer";
import WritingAIFeedbackModal from "./Components/WritingAIFeedbackModal/WritingAIFeedbackModal";
import { AppContext } from "../../Util/contexts/AppContext";
import ConfirmationPopup from "../../Components/ConfirmationPopup/ConfirmationPopup";
import GradingSection from "./Components/GradingSection";
import GradingNav from "./Components/GradingNav";
import GradingHeader from "./Components/GradingHeader";
import GradingScore from "./Components/GradingScore";
import GradingLoader from "./Components/GradingLoader";
import fetchCaseGrade from "./helperFunctions/fetchCaseGrade";
import checkCaseGrade from "./helperFunctions/checkCaseGrade";

export default class Grading extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reviewLater: this.props.reviewLater,
      gradingData: null,
      reviewLaterLoading: false,
      questionIdMap: null,
      fetchingGradeOutstanding: true,
      isInitialLoad: true, 
      resendingGrade: false,
      displayPDFViewer: false,
      pdfToDisplay: null,
      displayWritingFeedbackModal: false,
      writingFeedbackModalSection: null,
      currentlyPlayingAudioSegment: null,
      lastChecked: new Date(),
    };
  }

  static contextType = AppContext;

  componentDidMount() {
    this.addCopyProtection();

    if (!this._hasInitialized) {
      console.log("Initial fetch");
      this._hasInitialized = true;
      this.handleFetchCaseGrade(this.props.selectedExamGuid);
    }

    this.checkInterval = setInterval(async () => {
      // Safety check to ensure component is still mounted
      if (!this.checkInterval) return;

      console.log("Checking data", this.state.gradingData);

      try {
        const response = await this.checkCaseGradingStatus(
          this.props.selectedExamGuid
        );
        console.log("Got response from check grade", response);

        // Check if all grades are completed - do this check regardless of new grades
        const allGradesCompleted = this.areAllGradesCompleted();

        if (allGradesCompleted) {
          console.log("All grades completed - stopping interval");
          clearInterval(this.checkInterval);
          this.checkInterval = null;
          this.setState({
            fetchingGradeOutstanding: false,
            lastChecked: new Date(),
          });
          return;
        }

        let missingGrades = [];

        this.state.gradingData?.WritingGrades.forEach(function (grade) {
          if (grade.state === "processing") {
            missingGrades.push(grade.sectionId);
          }
        });

        this.state.gradingData?.SpeakingGrades.forEach(function (grade) {
          if (grade.state === "processing") {
            missingGrades.push(grade.sectionId);
          }
        });

        let newGradesAvailable = false; // Fixed typo

        response.forEach(function (gradeStatus) {
          if (
            gradeStatus.state != "processing" &&
            missingGrades.includes(gradeStatus.section_id)
          ) {
            console.log("New grade ready", gradeStatus);
            newGradesAvailable = true;
          }
        });

        if (newGradesAvailable) {
          console.log("Fetching case grade due to new grades");
          this.handleFetchCaseGrade(this.props.selectedExamGuid);
        } else {
          this.setState({ fetchingGradeOutstanding: false });
          console.log("No new grade available");
        }

        this.setState({ lastChecked: new Date() });
      } catch (error) {
        console.error("Error in check interval:", error);
        this.setState({ fetchingGradeOutstanding: false });
      }
    }, 20000);
  }

  // Helper method to check if all grades are completed
  areAllGradesCompleted = () => {
    if (!this.state.gradingData) return false;

    const hasProcessingWritingGrades =
      this.state.gradingData.WritingGrades?.some(
        (grade) => grade.state === "processing"
      );

    const hasProcessingSpeakingGrades =
      this.state.gradingData.SpeakingGrades?.some(
        (grade) => grade.state === "processing"
      );

    return !hasProcessingWritingGrades && !hasProcessingSpeakingGrades;
  };

  componentWillUnmount() {
    this.removeCopyProtection();

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  checkCaseGradingStatus = async (examGuid) => {    
    try {
      const { checkedData } = await checkCaseGrade(
        examGuid,
        this.context.route,
        this.context.userData,
        this.context.displayAuthenticationError
      );
      //this.setState({ fetchingGradeOutstanding: false });
      return checkedData;
    } catch (error) {
     // this.setState({ fetchingGradeOutstanding: false });
      console.log("Error fetching case grade", error);
    }
  };

  handleFetchCaseGrade = async (examGuid) => {
    this.setState({ fetchingGradeOutstanding: true });
    try {
      const { gradingData, questionIdMap } = await fetchCaseGrade(
        examGuid,
        this.context.route,
        this.context.userData,
        this.context.displayAuthenticationError
      );
      this.setState({
        gradingData,
        questionIdMap,
        isInitialLoad: false,
      });
      console.log("Actual grading data", gradingData);
      console.log(
        "Setting submission date to ",
        gradingData.DateCompletedWithTimezone
      );
      this.setState({
        submissionTime: new Date(gradingData.DateCompletedWithTimezone),
      });
    } catch (error) {
      console.log("Error fetching case grade", error);
    }
    this.setState({ fetchingGradeOutstanding: false });
  };

  refetchCaseGrade = async () => {
    // Track when resend was clicked, don't overwrite original submission time
    this.setState({
      fetchingGradeOutstanding: true,
    });

    try {
      const { gradingData, questionIdMap } = await fetchCaseGrade(
        this.props.selectedExamGuid,
        this.context.route,
        this.context.userData,
        this.context.displayAuthenticationError
      );
      this.setState({
        gradingData,
        questionIdMap,
      });
      console.log("Actual grading data", gradingData);
      console.log("Setting submission date to ", gradingData.DateCompleted);
    } catch (error) {
      console.log("Error fetching case grade", error);
    }
    this.setState({ fetchingGradeOutstanding: false });
  };

  resendCaseGrade = async (type, id) => {
    this.setState({
      resendingGrade: true,
    });
    await fetch(`${this.context.route}/resendgrading.webapi`, {
      method: "POST",
      headers: {
        Token: this.context.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify({
        guid: this.props.selectedExamGuid,
        gradetype: type,
        sectionId: id,
      }),
    });

    await fetch(`${this.context.route}/updatesendtoaitime.webapi`, {
      method: "POST",
      headers: {
        Token: this.context.userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify({
        guid: this.props.selectedExamGuid,
      }),
    });

    try {
      const { gradingData, questionIdMap } = await fetchCaseGrade(
        this.props.selectedExamGuid,
        this.context.route,
        this.context.userData,
        this.context.displayAuthenticationError
      );
      this.setState({
        gradingData,
        questionIdMap,
      });
    } catch (error) {
      console.log("Error fetching case grade", error);
    }

    console.log("Complete resending");
    this.setState({ resendingGrade: false });
  };

  openPDF = (pdfBase64) => {
    if (pdfBase64.startsWith("data:application/pdf;base64,")) {
      this.setState({ displayPDFViewer: true, pdfToDisplay: pdfBase64 });
      return;
    }

    // Remove double quotes from the base64 string
    pdfBase64 = pdfBase64.replace(/"/g, "");
    let pdf = "data:application/pdf;base64," + pdfBase64;
    this.setState({ displayPDFViewer: true, pdfToDisplay: pdf });
  };

  addCopyProtection = () => {
    document.addEventListener("copy", this.preventCopy);
    document.addEventListener("cut", this.preventCopy);
    document.addEventListener("selectstart", this.preventSelect);
    document.addEventListener("contextmenu", this.preventContextMenu);
    document.addEventListener("keydown", this.preventKeyboardShortcuts);

    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";
  };

  removeCopyProtection = () => {
    document.removeEventListener("copy", this.preventCopy);
    document.removeEventListener("cut", this.preventCopy);
    document.removeEventListener("selectstart", this.preventSelect);
    document.removeEventListener("contextmenu", this.preventContextMenu);
    document.removeEventListener("keydown", this.preventKeyboardShortcuts);

    document.body.style.userSelect = "";
    document.body.style.webkitUserSelect = "";
  };

  preventCopy = (e) => {
    e.preventDefault();
    return false;
  };

  preventSelect = (e) => {
    e.preventDefault();
    return false;
  };

  preventContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  preventKeyboardShortcuts = (e) => {
    if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "c" ||
        e.key === "C" ||
        e.key === "v" ||
        e.key === "V" ||
        e.key === "a" ||
        e.key === "A")
    ) {
      e.preventDefault();
      return false;
    }
    if (e.key === "F12") {
      e.preventDefault();
      return false;
    }
  };

  render() {
    return (
      <div
        className="newGradingPageWrapper"
        style={{ userSelect: "none", WebkitUserSelect: "none" }}
        onContextMenu={(e) => e.preventDefault()}
        onSelectStart={(e) => e.preventDefault()}
      >
        <nav className="newGradingNavbar">
          <h1 className="caseListLogoText">
            OCC<span className="caseListLogoTextNormal">English</span>
          </h1>
        </nav>
        <AnimatePresence>
          {this.state.isInitialLoad ? (
            <GradingLoader key="gradingLoader" />
          ) : (
            <>
              <GradingNav
                closeGrading={this.props.closeGrading}
                gradingData={this.state.gradingData}
                reviewLaterLoading={this.state.reviewLaterLoading}
                setReviewLaterLoading={(loading) =>
                  this.setState({ reviewLaterLoading: loading })
                }
                key="newGradingNavContainerTopOfPage"
              />
              <GradingHeader
                gradingData={this.state.gradingData}
                key="newGradingCaseTitleContainer"
                shouldShowPreviousCompletionsButton={
                  this.props.previousPage !== "Simulation"
                }
              />
              <GradingScore
                gradingData={this.state.gradingData}
                key="newGradingCaseDetailsWrapper"
              />
              {this.state.gradingData.Sections.map((section, sectionIndex) => {
                const firstAttemptObject =
                  this.state.gradingData.AvgFirstAttempts.find(
                    (element) => element.sectionId == section.sectionId
                  );

                return (
                  <GradingSection
                    sectionIndex={sectionIndex}
                    section={section}
                    averageFirstAttemptGrade={
                      firstAttemptObject != undefined
                        ? firstAttemptObject.averageFirstAttemptGrade
                        : 0
                    }
                    currentlyPlayingAudioSegment={
                      this.state.currentlyPlayingAudioSegment
                    }
                    questionIdMap={this.state.questionIdMap}
                    gradingData={this.state.gradingData}
                    setCurrentlyPlayingAudioSegment={(audioRef) =>
                      this.setState({ currentlyPlayingAudioSegment: audioRef })
                    }
                    handleDisplayWritingFeedbackModal={() =>
                      this.setState({
                        displayWritingFeedbackModal: true,
                        writingFeedbackModalSection: section,
                      })
                    }
                    openPDF={this.openPDF}
                    key={`gradingFeedbackSectionContainer - ${sectionIndex}`}
                    lastChecked={this.state.lastChecked}
                    resendCaseGrade={this.resendCaseGrade}
                    resendingGrade={this.state.resendingGrade}
                  />
                );
              })}
              <GradingNav
                closeGrading={this.props.closeGrading}
                gradingData={this.state.gradingData}
                reviewLaterLoading={this.state.reviewLaterLoading}
                setReviewLaterLoading={(loading) =>
                  this.setState({ reviewLaterLoading: loading })
                }
                key="newGradingNavContainerBottomOfPage"
              />
            </>
          )}

          {this.state.displayWritingFeedbackModal && (
            <WritingAIFeedbackModal
              userData={this.context.userData}
              route={this.context.route}
              sectionData={this.state.writingFeedbackModalSection}
              closeModal={() =>
                this.setState({
                  displayWritingFeedbackModal: false,
                  writingFeedbackModalSection: null,
                })
              }
              animationsDisabled={this.context.userData.AnimationDisabled}
              primaryColor={this.context.primaryColor}
              key="writingFeedbackModal"
            />
          )}

          {this.state.displayPDFViewer && (
            <NewPdfViewer
              pdfFile={this.state.pdfToDisplay}
              onClose={() =>
                this.setState({ displayPDFViewer: false, pdfToDisplay: null })
              }
              pdfDocked={false}
              handlePdfDock={() => null}
              page={"grading"}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
}
